import { useRef, useState } from "react";
import { Upload, FolderOpen, Loader2, X as XIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import DrivePicker from "@/components/google/DrivePicker";

const ACCEPT = ".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp";
const MAX_BYTES = 25 * 1024 * 1024;
const TYPES = new Set(["image/jpeg", "image/jpg", "image/png", "image/webp"]);

/**
 * Lets a user attach an image either from their device or from Google Drive.
 * Both paths produce a direct public URL hosted by Mediana Hub that Webflow
 * accepts. Calls `onUploaded(url)` once the URL is ready.
 */
export default function ImageUploader({
  onUploaded,
  disabled,
  label = "Subir imagen",
  compact = false,
  testid = "image-uploader",
  driveTitle = "Elegir imagen en Google Drive",
  showDrive = true,
}) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [drive, setDrive] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    if (!TYPES.has(file.type)) {
      toast.error("Formato no soportado. Usa JPG, PNG o WebP.");
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error(`La imagen pesa ${(file.size / (1024 * 1024)).toFixed(1)} MB. El máximo es 25 MB.`);
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/uploads/image", form, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded?.(data.url, data);
      toast.success("Imagen subida");
    } catch (e) {
      toast.error(errMsg(e, "No se pudo subir la imagen"));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const handleDrivePick = async (f) => {
    setBusy(true);
    try {
      const { data } = await api.post("/uploads/from-drive", { fileId: f.id });
      onUploaded?.(data.url, data);
      toast.success("Imagen traída de Drive");
    } catch (e) {
      toast.error(errMsg(e, "No se pudo copiar la imagen de Drive"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className={compact ? "flex items-center gap-1.5" : "flex flex-wrap items-center gap-2"} data-testid={testid}>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          className="hidden"
          disabled={disabled || busy}
          onChange={(e) => handleFile(e.target.files?.[0])}
          data-testid={`${testid}-file-input`}
        />
        <Button
          type="button"
          size={compact ? "icon" : "sm"}
          variant="outline"
          disabled={disabled || busy}
          onClick={() => inputRef.current?.click()}
          data-testid={`${testid}-device-button`}
          className={compact ? "h-8 w-8" : ""}
          aria-label={label}
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
          {!compact && <span className="ml-1">{busy ? "Subiendo…" : label}</span>}
        </Button>
        {showDrive && (
          <Button
            type="button"
            size={compact ? "icon" : "sm"}
            variant="outline"
            disabled={disabled || busy}
            onClick={() => setDrive(true)}
            data-testid={`${testid}-drive-button`}
            className={compact ? "h-8 w-8" : ""}
            aria-label="Elegir de Drive"
          >
            <FolderOpen className="h-3.5 w-3.5" />
            {!compact && <span className="ml-1">Drive</span>}
          </Button>
        )}
      </div>
      {showDrive && (
        <DrivePicker
          open={drive}
          onOpenChange={setDrive}
          title={driveTitle}
          filterMime={["image/"]}
          onPick={handleDrivePick}
        />
      )}
    </>
  );
}

export { XIcon };
